<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Success state -->
      <b-card v-if="emailSent" header="Registration Submitted" header-bg-variant="success" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          A verification email has been sent to <strong>{{ sentEmail }}</strong
          >. Follow the instructions in the email to complete registration within the next <strong>48 hours</strong>.
        </p>

        <b-alert v-if="emailShared" show variant="warning" class="mb-3" style="font-size:0.9rem;">
          <strong>The email address provided {{ sentEmail }} is being used by another account.</strong>
          Please update your contact information after you log in.<br /><br />
          <strong>IMPORTANT:</strong> If you do not update the email address, please make sure you have access to this email. You will need access to
          the email to reset your password or request your username.
        </b-alert>

        <hr />

        <p class="mb-2" style="font-size:0.9rem;">
          If you do not receive the email within the next <strong>15 minutes</strong>, please check the following:
        </p>
        <ul class="checklist mb-3">
          <li>
            Verify that the email address provided is correct. If not,
            <a :href="`${memberPortalUrl}/app/account/create`">CLICK HERE</a> to re-register.
          </li>
          <li>Look for the e-mail in the Junk/Spam folder.</li>
          <li>
            <a :href="`${memberPortalUrl}/app/account/create`">CLICK HERE</a>
            to re-register using an email address from a different email provider.
          </li>
        </ul>

        <div class="text-right">
          <b-button size="sm" variant="primary" :to="{ name: 'login' }">Back to Login</b-button>
        </div>
      </b-card>

      <!-- Registration form -->
      <b-card v-else header="Create Account" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <b-form @submit.prevent="onSubmit">
          <b-form-group label="UBC ID:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ubcid" v-mask="'U-####-####'" :state="validated('ubcid')" placeholder="e.g. U-1234-5678" trim @paste="onUbcIdPaste" />
            <b-form-invalid-feedback>A valid UBC ID is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Last Name:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.lastname" :state="validated('lastname')" placeholder="Last Name" trim />
            <b-form-invalid-feedback>Last Name is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Date of Birth:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.birthdate" v-mask="'##/##/####'" :state="validated('birthdate')" placeholder="MM/DD/YYYY" trim />
            <b-form-invalid-feedback>Invalid date</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Email:" label-cols-sm="4" label-align-sm="right">
            <b-input type="email" v-model="form.email" :state="validated('email')" placeholder="Email Address" trim />
            <b-form-invalid-feedback>A valid email address is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Username:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.username" :state="validated('username')" placeholder="Username" trim />
            <b-form-invalid-feedback>Username must be at least 6 characters and contain at least one letter</b-form-invalid-feedback>
          </b-form-group>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Send Activation Email
            </b-button>
          </div>
        </b-form>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { emailValidator } from '@/util/validator';
import { isValid as isValidUbcId } from '@/util/ubc_id';
import { isValidDobString } from '@/util/date';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import recaptchaMixin from '@/mixins/recaptcha_fallback';
import { renderErrorBody } from '@/util/error';

@Component({
  metaInfo: {
    title: 'Member Portal - Create Account'
  },
  components: { RecaptchaV2Modal }
})
export default class AccountCreation extends Mixins(recaptchaMixin) {
  form = {
    ubcid: 'U',
    lastname: '',
    birthdate: '',
    email: '',
    username: ''
  };

  loading = false;
  submitted = false;
  emailSent = false;
  sentEmail = '';
  emailShared = false;

  get memberPortalUrl() {
    return this.$store.getters.meta?.memberPortalUrl || 'https://member-portal.carpenters.org';
  }

  get submitDisabled() {
    return ['ubcid', 'lastname', 'birthdate', 'email', 'username'].some(k => !this.validated(k));
  }

  validated(field) {
    if (!this.submitted && !this.form[field]) return undefined;
    const value = (this.form[field] || '').trim();
    switch (field) {
      case 'ubcid':
        return isValidUbcId(value);
      case 'lastname':
        return value.length > 0;
      case 'birthdate':
        return isValidDobString(value);
      case 'email':
        return emailValidator(value);
      case 'username':
        return value.length >= 6 && /[a-zA-Z]/.test(value);
      default:
        return true;
    }
  }

  mounted() {
    this.loadRecaptcha();
  }

  onUbcIdPaste(e) {
    e.preventDefault();
    const raw = (e.clipboardData || window.clipboardData).getData('text');
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let val = 'U';
    if (digits.length > 0) val += '-' + digits.slice(0, 4);
    if (digits.length > 4) val += '-' + digits.slice(4);
    this.form.ubcid = val;
  }

  resetForm() {
    this.emailSent = false;
    this.emailShared = false;
    this.sentEmail = '';
    this.submitted = false;
    this.form = { ubcid: 'U', lastname: '', birthdate: '', email: '', username: '' };
  }

  async onSubmit() {
    try {
      this.submitted = true;

      if (this.submitDisabled) {
        return this.$bvToast.toast('Please correct the highlighted errors.', {
          title: 'Error',
          solid: true,
          variant: 'danger'
        });
      }

      this.loading = true;

      const result = await this.submitWithRecaptcha('create_account', tokens =>
        memberDao.createAccount({
          ubcid: this.form.ubcid.trim(),
          lastname: this.form.lastname.trim(),
          birthdate: this.form.birthdate.trim(),
          email: this.form.email.trim(),
          username: this.form.username.trim(),
          ...tokens
        })
      );
      this.sentEmail = this.form.email.trim();
      this.emailShared = !!(result && result.email_shared);
      this.emailSent = true;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Unable to Create Account',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped>
.checklist {
  padding-left: 1.25rem;
  font-size: 0.9rem;
}
.checklist li {
  margin-bottom: 0.35rem;
}
</style>
